/**
 * useContactsDatabase.ts
 * 
 * Hook que adapta useContactsWASP a la interfaz ContactDB.
 * Mantiene compatibilidad con componentes existentes mientras usa API real.
 * 
 * @version 3.0.1 - WASP Integration + Geomarketing
 * @date 2024-11-13
 * 
 * CHANGELOG v3.0.1:
 * - ✅ Soporte para campos de geolocalización (address, location, locationStatus)
 * - ✅ Actualización de adaptWASPtoContactDB para preservar datos GPS
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useUserContext } from '../layouts/UserContext';
import { useContactsWASP, type ContactWASP } from './useContactsWASP';
import {
  ContactDB,
  ContactGroup,
  ContactTag,
  ContactStats,
  MOCK_GROUPS,
  MOCK_TAGS
} from '../data/mockContactsDatabase';

// ==================== 🔄 ADAPTADORES ====================

/**
 * Convierte ContactWASP a ContactDB
 * ✅ v3.0.1: Preserva campos de geolocalización si existen
 */
const adaptWASPtoContactDB = (wasp: ContactWASP): ContactDB => {
  // Base de datos incremental híbrida: preservar campos opcionales
  const base: ContactDB = {
    id: wasp.id,
    phone: wasp.phone,
    name: wasp.name,
    email: wasp.email || undefined,
    company: wasp.company || undefined,
    positon: wasp.position || undefined, // ContactDB usa "positon" (typo)
    groups: wasp.groups || [],
    tags: wasp.tags || [],
    status: wasp.status === 'blocked' ? 'inactive' : 
            wasp.status === 'pending' ? 'inactive' :
            wasp.status as 'active' | 'inactive' | 'bounced' | 'unsubscribed',
    notes: wasp.notes || undefined,
    source: wasp.source === 'csv' ? 'import' : 
            wasp.source === 'conversation' ? 'webform' :
            wasp.source as 'manual' | 'import' | 'api' | 'webform' | 'oneshot',
    created_at: wasp.created_at,
    updated_at: wasp.updated_at,
    last_contacted: wasp.updated_at,
    metadata: wasp.metadata || {}
  };
  
  // ✅ NUEVO: Preservar geolocalización si existe en metadata
  if (wasp.metadata) {
    if (wasp.metadata.address) {
      base.address = wasp.metadata.address;
    }
    
    if (wasp.metadata.location) {
      base.location = wasp.metadata.location;
    }
    
    if (wasp.metadata.locationStatus) {
      base.locationStatus = wasp.metadata.locationStatus;
    }
  }
  
  // Si no tiene locationStatus definido, inferir de datos disponibles
  if (!base.locationStatus) {
    if (base.location) {
      base.locationStatus = base.location.source === 'shared' ? 'shared' : 'registered';
    } else {
      base.locationStatus = 'missing';
    }
  }
  
  return base;
};

/**
 * Convierte ContactDB a Partial<ContactWASP>
 * ✅ v3.0.1: Guarda geolocalización en metadata
 */
const adaptContactDBtoWASP = (
  db: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>
): Partial<ContactWASP> => {
  const base: Partial<ContactWASP> = {
    phone: db.phone,
    name: db.name,
    email: db.email,
    company: db.company,
    position: db.positon, // ContactDB usa "positon" (typo)
    groups: db.groups || [],
    tags: db.tags || [],
    status: db.status === 'bounced' || db.status === 'unsubscribed' 
      ? 'inactive' 
      : db.status as 'active' | 'inactive',
    notes: db.notes,
    source: db.source === 'webform' ? 'conversation' :
            db.source === 'oneshot' ? 'manual' :
            db.source as 'manual' | 'csv' | 'ai' | 'import' | 'conversation' | 'api',
    metadata: {
      ...(db.metadata || {})
    }
  };
  
  // ✅ NUEVO: Guardar geolocalización en metadata
  if (db.address) {
    base.metadata!.address = db.address;
  }
  
  if (db.location) {
    base.metadata!.location = db.location;
  }
  
  if (db.locationStatus) {
    base.metadata!.locationStatus = db.locationStatus;
  }
  
  return base;
};

// ==================== 📋 INTERFACES ====================

export interface ContactFilters {
  search?: string;
  status?: 'all' | 'active' | 'inactive' | 'bounced' | 'unsubscribed';
  groups?: string[];
  tags?: string[];
  locationStatus?: ('registered' | 'shared' | 'missing')[]; // ✅ NUEVO
}

export interface UseContactsDatabaseResult {
  // Datos
  contacts: ContactDB[];
  groups: ContactGroup[];
  tags: ContactTag[];
  stats: ContactStats;
  
  // Estados
  isLoading: boolean;
  error: string | null;
  
  // Acciones
  loadContacts: (filters?: ContactFilters) => Promise<void>;
  loadGroups: () => Promise<void>;
  loadTags: () => Promise<void>;
  refreshStats: () => void;
  
  // CRUD Contactos
  getContactById: (id: string) => ContactDB | undefined;
  checkDuplicatePhone: (phone: string) => ContactDB | undefined;
  addContact: (contact: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>) => Promise<ContactDB>;
  updateContact: (id: string, updates: Partial<ContactDB>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  
  // ✅ NUEVO: Actualizar solo geolocalización
  updateContactLocation: (
    id: string, 
    location: ContactDB['location'], 
    address?: ContactDB['address']
  ) => Promise<void>;
  
  // Búsqueda y filtrado
  searchContactsInDB: (query: string) => ContactDB[];
  filterByGroup: (groupId: string) => ContactDB[];
  filterByTag: (tagId: string) => ContactDB[];
  filterByLocationStatus: (status: 'registered' | 'shared' | 'missing') => ContactDB[]; // ✅ NUEVO
  getContactsWithLocation: () => ContactDB[]; // ✅ NUEVO
  
  // Selección múltiple
  selectedContacts: string[];
  toggleContactSelection: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  getSelectedContactsData: () => ContactDB[];
}

// ==================== 🎣 HOOK ====================

export const useContactsDatabase = (): UseContactsDatabaseResult => {
  
  // ───────────────────────────────────────────────────────────────────────
  // HOOKS
  // ───────────────────────────────────────────────────────────────────────
  
  const { user } = useUserContext();
  
  const {
    contacts: waspContacts,
    isLoading,
    error,
    addContact: addContactWASP,
    updateContact: updateContactWASP,
    deleteContact: deleteContactWASP,
    getContactById: getContactByIdWASP,
    getContactByPhone: getContactByPhoneWASP,
    searchContacts: searchContactsWASP,
    refresh
  } = useContactsWASP(user?._id || 'unknown');
  
  // ───────────────────────────────────────────────────────────────────────
  // ESTADOS LOCALES
  // ───────────────────────────────────────────────────────────────────────
  
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [groups] = useState<ContactGroup[]>(MOCK_GROUPS);
  const [tags] = useState<ContactTag[]>(MOCK_TAGS);
  
  // ───────────────────────────────────────────────────────────────────────
  // ADAPTACIÓN DE DATOS
  // ───────────────────────────────────────────────────────────────────────
  
  /**
   * Contactos adaptados de WASP a ContactDB
   */
  const contacts = useMemo(() => {
    return waspContacts.map(adaptWASPtoContactDB);
  }, [waspContacts]);
  
  /**
   * Estadísticas calculadas
   */
  const stats = useMemo((): ContactStats => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    return {
      total: contacts.length,
      active: contacts.filter(c => c.status === 'active').length,
      inactive: contacts.filter(c => c.status === 'inactive').length,
      bounced: contacts.filter(c => c.status === 'bounced').length,
      unsubscribed: contacts.filter(c => c.status === 'unsubscribed').length,
      new_this_week: contacts.filter(c => new Date(c.created_at) >= oneWeekAgo).length,
      new_this_month: contacts.filter(c => new Date(c.created_at) >= oneMonthAgo).length
    };
  }, [contacts]);
  
  // ───────────────────────────────────────────────────────────────────────
  // LOAD DATA
  // ───────────────────────────────────────────────────────────────────────
  
  const loadContacts = useCallback(async (filters?: ContactFilters) => {
    await refresh();
  }, [refresh]);
  
  const loadGroups = useCallback(async () => {
    // Grupos están en estado local (MOCK_GROUPS)
  }, []);
  
  const loadTags = useCallback(async () => {
    // Tags están en estado local (MOCK_TAGS)
  }, []);
  
  const refreshStats = useCallback(() => {
    // Stats se calculan automáticamente via useMemo
  }, []);
  
  // ───────────────────────────────────────────────────────────────────────
  // BÚSQUEDA Y FILTRADO
  // ───────────────────────────────────────────────────────────────────────
  
  const searchContactsInDB = useCallback((query: string): ContactDB[] => {
    const search = query.toLowerCase();
    return contacts.filter(c =>
      c.name.toLowerCase().includes(search) ||
      c.phone.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.company?.toLowerCase().includes(search)
    );
  }, [contacts]);
  
  const filterByGroup = useCallback((groupId: string): ContactDB[] => {
    return contacts.filter(c => c.groups.includes(groupId));
  }, [contacts]);
  
  const filterByTag = useCallback((tagId: string): ContactDB[] => {
    return contacts.filter(c => c.tags.includes(tagId));
  }, [contacts]);
  
  /**
   * ✅ NUEVO: Filtrar por estado de ubicación
   */
  const filterByLocationStatus = useCallback((
    status: 'registered' | 'shared' | 'missing'
  ): ContactDB[] => {
    return contacts.filter(c => c.locationStatus === status);
  }, [contacts]);
  
  /**
   * ✅ NUEVO: Obtener solo contactos con ubicación válida
   */
  const getContactsWithLocation = useCallback((): ContactDB[] => {
    return contacts.filter(c => c.location && c.location.lat && c.location.lng);
  }, [contacts]);
  
  // ───────────────────────────────────────────────────────────────────────
  // CRUD OPERATIONS
  // ───────────────────────────────────────────────────────────────────────
  
  const getContactById = useCallback((id: string): ContactDB | undefined => {
    return contacts.find(c => c.id === id);
  }, [contacts]);
  
  const checkDuplicatePhone = useCallback((phone: string): ContactDB | undefined => {
    return contacts.find(c => c.phone === phone);
  }, [contacts]);
  
  const addContact = useCallback(async (
    contact: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>
  ): Promise<ContactDB> => {
    const waspData = adaptContactDBtoWASP(contact);
    const newContact = await addContactWASP(waspData);
    
    if (!newContact) {
      throw new Error('Failed to add contact');
    }
    
    return adaptWASPtoContactDB(newContact);
  }, [addContactWASP]);
  
  const updateContact = useCallback(async (
    id: string,
    updates: Partial<ContactDB>
  ): Promise<void> => {
    const waspUpdates: Partial<ContactWASP> = {
      name: updates.name,
      email: updates.email,
      company: updates.company,
      position: updates.positon,
      groups: updates.groups,
      tags: updates.tags,
      status: updates.status === 'bounced' || updates.status === 'unsubscribed'
        ? 'inactive'
        : updates.status as 'active' | 'inactive' | undefined,
      notes: updates.notes,
      metadata: {
        ...(updates.metadata || {})
      }
    };
    
    // ✅ NUEVO: Preservar geolocalización en metadata
    if (updates.address) {
      waspUpdates.metadata!.address = updates.address;
    }
    if (updates.location) {
      waspUpdates.metadata!.location = updates.location;
    }
    if (updates.locationStatus) {
      waspUpdates.metadata!.locationStatus = updates.locationStatus;
    }
    
    const result = await updateContactWASP(id, waspUpdates);
    
    if (!result) {
      throw new Error('Failed to update contact');
    }
  }, [updateContactWASP]);
  
  /**
   * ✅ NUEVO: Actualizar solo geolocalización
   * Útil para no sobrescribir otros campos
   */
  const updateContactLocation = useCallback(async (
    id: string,
    location: ContactDB['location'],
    address?: ContactDB['address']
  ): Promise<void> => {
    const updates: Partial<ContactDB> = {
      location,
      locationStatus: location 
        ? (location.source === 'shared' ? 'shared' : 'registered')
        : 'missing'
    };
    
    if (address) {
      updates.address = address;
    }
    
    await updateContact(id, updates);
  }, [updateContact]);
  
  const deleteContact = useCallback(async (id: string): Promise<void> => {
    const success = await deleteContactWASP(id);
    
    if (!success) {
      throw new Error('Failed to delete contact');
    }
    
    setSelectedContacts(prev => prev.filter(cId => cId !== id));
  }, [deleteContactWASP]);
  
  // ───────────────────────────────────────────────────────────────────────
  // SELECCIÓN MÚLTIPLE
  // ───────────────────────────────────────────────────────────────────────
  
  const toggleContactSelection = useCallback((id: string) => {
    setSelectedContacts(prev => 
      prev.includes(id) 
        ? prev.filter(cId => cId !== id)
        : [...prev, id]
    );
  }, []);
  
  const selectAll = useCallback(() => {
    setSelectedContacts(contacts.map(c => c.id));
  }, [contacts]);
  
  const clearSelection = useCallback(() => {
    setSelectedContacts([]);
  }, []);
  
  const getSelectedContactsData = useCallback((): ContactDB[] => {
    return contacts.filter(c => selectedContacts.includes(c.id));
  }, [contacts, selectedContacts]);
  
  // ───────────────────────────────────────────────────────────────────────
  // RETURN
  // ───────────────────────────────────────────────────────────────────────
  
  return {
    // Datos
    contacts,
    groups,
    tags,
    stats,
    
    // Estados
    isLoading,
    error,
    
    // Acciones
    loadContacts,
    loadGroups,
    loadTags,
    refreshStats,
    
    // CRUD
    getContactById,
    checkDuplicatePhone,
    addContact,
    updateContact,
    deleteContact,
    updateContactLocation, // ✅ NUEVO
    
    // Búsqueda
    searchContactsInDB,
    filterByGroup,
    filterByTag,
    filterByLocationStatus, // ✅ NUEVO
    getContactsWithLocation, // ✅ NUEVO
    
    // Selección
    selectedContacts,
    toggleContactSelection,
    selectAll,
    clearSelection,
    getSelectedContactsData
  };
};

export default useContactsDatabase;